import * as React from "react";
import { Button, makeStyles, Tooltip } from "@fluentui/react-components";
import {
  ArrowClockwise16Regular,
  ArrowDown16Regular,
  Chat16Regular,
  Copy16Regular,
  Edit16Regular,
} from "@fluentui/react-icons";
import DislikeIcon from "../icons/DislikeIcon";
import { spinAnimation } from "@/utils/animations";

const useStyles = makeStyles({
  actions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",
    paddingTop: "12px",
    marginTop: "8px",
  },
  actionsLeft: {
    display: "flex",
    gap: "4px",
    alignItems: "center",
  },
  actionsRight: {
    display: "flex",
    gap: "4px",
    alignItems: "center",
  },
  iconButton: {
    minWidth: "32px",
    minHeight: "32px",
    padding: "4px",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#333",
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
    "&:disabled": {
      opacity: 0.5,
      cursor: "not-allowed",
    },
  },
  spinningIcon: {
    animationName: spinAnimation,
    animationDuration: "1s",
    animationIterationCount: "infinite",
    animationTimingFunction: "linear",
  },
});

type CardActionsProps = {
  isChatVariant: boolean;
  isDisliked: boolean;
  isRegenerating: boolean;
  isDislikeLoading: boolean;
  onInsert: () => void;
  onCopy: () => void;
  onNewChat: () => void;
  onRegenerate: () => void;
  onDislike: () => void;
  onAnswerEdit?: () => void;
  teamReviewActions?: React.ReactNode;
};

export const CardActions: React.FC<CardActionsProps> = ({
  isChatVariant,
  isDisliked,
  isRegenerating,
  isDislikeLoading,
  onInsert,
  onCopy,
  onNewChat,
  onRegenerate,
  onDislike,
  onAnswerEdit,
  teamReviewActions,
}) => {
  const styles = useStyles();

  return (
    <div className={styles.actions}>
      <div className={styles.actionsLeft}>
        <Tooltip content="Insert Below" relationship="label">
          <Button icon={<ArrowDown16Regular />} onClick={onInsert} className={styles.iconButton} />
        </Tooltip>
        <Tooltip content="Copy Answer" relationship="label">
          <Button icon={<Copy16Regular />} onClick={onCopy} className={styles.iconButton} />
        </Tooltip>
        {!isChatVariant && (
          <Tooltip content="New Chat" relationship="label">
            <Button icon={<Chat16Regular />} onClick={onNewChat} className={styles.iconButton} />
          </Tooltip>
        )}
        {onAnswerEdit && (
          <Tooltip content="Edit Answer" relationship="label">
            <Button icon={<Edit16Regular />} onClick={onAnswerEdit} className={styles.iconButton} />
          </Tooltip>
        )}
      </div>

      {/* Chat responses have no persisted worksheet query to regenerate or review. */}
      {!isChatVariant && (
        <div className={styles.actionsRight}>
          {teamReviewActions}
          <Tooltip content="Regenerate answer" relationship="label">
            <Button
              icon={<ArrowClockwise16Regular className={isRegenerating ? styles.spinningIcon : undefined} />}
              onClick={onRegenerate}
              disabled={isRegenerating}
              className={styles.iconButton}
            />
          </Tooltip>
          <Tooltip content={isDisliked ? "Undo Dislike answer" : "Dislike answer"} relationship="label">
            <Button
              icon={<DislikeIcon />}
              onClick={onDislike}
              disabled={isDislikeLoading}
              className={styles.iconButton}
              style={isDisliked ? { color: "#d13438" } : undefined}
            />
          </Tooltip>
        </div>
      )}
    </div>
  );
};
